import { useMutation, useQuery } from '@apollo/client';

import { CardViewSkeleton } from '@/components/elements/_skeletons/card-view.skeleton';
import { TableViewSkeleton } from '@/components/elements/_skeletons/table-view.skeleton';
import { GeneralCard } from '@/components/elements/general-card/general-card.component';
import { T } from '@/components/i18n/message.i18n.component';
import { GridTableLayout } from '@/components/layout/grid-table/grid-table.layout.component';
import { ProductGroup } from '@/gql/graphql';
import { useProductGroups } from '@/hooks/use-product-groups/use-product-groups.hook';
import { useToast } from '@/hooks/use-toast.hook';
import { useUser } from '@/hooks/use-user/use-user.hook';
import { isProductGroupDeleteEnabled, isProductGroupEditEnabled } from '@/lib/product-group.utils';
import { useDialogStore } from '@/store/dialog.store';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { productCardGroupMessages } from './components/product-group-card.messages';
import { GroupProductFormInitialValues, ProductGroupForm } from './components/product-group-form/product-group-form';
import { ProductGroupTable } from './components/product-group-table.component';
import { ProductGroupsNoResults } from './product-group.no-results';
import { allProductGroups, deleteProductGroup } from './product-group.requests';
import {
  productGroupDialogTranslations,
  productGroupsPageMessages,
  productGroupsToastTranslations,
} from './product-groups.messages';

export const ProductGroupsPage = () => {
  const t = useIntl();
  const { guid } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { openDialog, closeDialog, setLoading: setDialogLoading } = useDialogStore();

  const { data } = useQuery<{ productGroups: ProductGroup[] }>(allProductGroups);
  const { productGroups, loading, search, filter, pageView, setPageView, handleFilterChange, handleSearchChange } =
    useProductGroups({
      initialProductGroups: data?.productGroups,
    });
  const [deleteProductGroupMutation] = useMutation(deleteProductGroup);

  const handleProductGroupAddDialog = () => {
    openDialog({
      type: 'custom',
      title: <T {...productGroupDialogTranslations.addProductTitle} />,
      element: <ProductGroupForm />,
    });
  };

  const openEditDialog = (productGroup: ProductGroup) => {
    openDialog({
      type: 'custom',
      title: <T {...productGroupDialogTranslations.editProductTitle} />,
      element: <ProductGroupForm edit initialValues={productGroup as Partial<GroupProductFormInitialValues>} />,
    });
  };

  const openConfirmationDialog = (productGroup: ProductGroup) => {
    openDialog({
      type: 'confirmation',
      item: { id: productGroup.id, name: productGroup.name },
      onConfirm: async () => {
        setDialogLoading(true);
        await deleteProductGroupMutation({
          variables: {
            id: productGroup.id,
          },
          refetchQueries: [{ query: allProductGroups }],
        });
        closeDialog();
        toast({
          description: <T {...productGroupsToastTranslations.deleteProductToast} />,
          variant: 'default',
        });
      },
    });
  };

  const handleProductGroupNavigation = (id: string) => {
    navigate(`/products/${id}`);
  };

  const handleViewChange = () => {
    setPageView(pageView === 'grid' ? 'table' : 'grid');
  };

  return (
    <GridTableLayout
      context="product-groups"
      loading={loading}
      loadingComponent={
        pageView === 'grid' ? (
          <CardViewSkeleton data-testid="product-page-loading-skeleton" />
        ) : (
          <TableViewSkeleton data-testid="product-page-loading-skeleton" />
        )
      }
      noResults={!loading && productGroups.length === 0}
      noResultsComponent={
        <ProductGroupsNoResults
          search={search}
          loading={loading}
          productGroups={productGroups}
          onProductGroupAdd={handleProductGroupAddDialog}
        />
      }
      actionBarConfig={{
        searchPlaceholder: t.formatMessage(productGroupsPageMessages.searchBarPlaceholder),
        filterBarText: <T {...productGroupsPageMessages.filterBarText} />,
        filterBarOptionAllProducts: <T {...productGroupsPageMessages.filterBarOptionAllProducts} />,
        filterBarOptionMyProducts: <T {...productGroupsPageMessages.filterBarOptionMyProducts} />,
        tableViewButton: <T {...productGroupsPageMessages.tableViewButton} />,
        gridViewButton: <T {...productGroupsPageMessages.gridViewButton} />,
        selectedView: pageView,
        handleViewChange,
        filter,
        handleFilterChange,
        handleSearchChange,
      }}
      navigationBarConfig={{
        productsTitle: <T {...productGroupsPageMessages.title} />,
        templatesTitle: <T {...productGroupsPageMessages.templates} />,
        addButtonTitle: <T {...productGroupsPageMessages.addProduct} />,
        addButtonAction: handleProductGroupAddDialog,
      }}
    >
      {pageView === 'grid' &&
        productGroups.map((productGroup) => (
          <GeneralCard
            onCardClick={handleProductGroupNavigation}
            id={productGroup.id}
            key={productGroup.id}
            context="product-group"
            title={productGroup.name}
            description={productGroup.description}
            updatedAt={productGroup.updatedAt}
            rows={[
              {
                name: 'version',
                label: <T {...productCardGroupMessages.versions} />,
                value: productGroup.members.length.toString(),
              },
            ]}
            menu={[
              {
                name: 'edit',
                disabled: !isProductGroupEditEnabled(guid, productGroup),
                label: <T {...productCardGroupMessages.editProduct} />,
                handleClick: () => openEditDialog(productGroup),
              },
              {
                name: 'delete',
                disabled: !isProductGroupDeleteEnabled(guid, productGroup),
                label: <T {...productCardGroupMessages.deleteProduct} />,
                handleClick: () => openConfirmationDialog(productGroup),
                red: true,
              },
            ]}
          />
        ))}
      {pageView === 'table' && (
        <ProductGroupTable
          data={productGroups}
          onRowClick={handleProductGroupNavigation}
          handleEdit={openEditDialog}
          handleDelete={openConfirmationDialog}
        />
      )}
    </GridTableLayout>
  );
};
